import { authTokens, getBackendLang } from '@shared/auth/authTokens'
import { API_BASE } from '@shared/config/constants'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RequestBody = Record<string, any> | FormData | null | undefined

interface FetchOptions {
  params?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
  // Parse the success body as a binary Blob instead of JSON — for file/PDF/xlsx
  // downloads. The auth, 401-refresh, language and error handling all still apply.
  responseType?: 'blob'
}

export interface ApiError extends Error {
  response?: {
    status?: number
    data?: unknown
    /** Сколько ждать до повтора, из заголовка `Retry-After` (429 и 503).
     *  `undefined`, если заголовка не было или он нечитаем — см.
     *  `retryAfterMs`. */
    retryAfterMs?: number
  }
}

/** Сообщение ошибки, которой клиент заканчивает неудачное обновление токена. */
const SESSION_EXPIRED_MESSAGE = 'Unauthorized'

/** Сессия истекла: клиент сам увёл на /login и бросил ошибку без ответа сервера. */
export function isSessionExpiredError(error: unknown): boolean {
  return error instanceof Error && error.message === SESSION_EXPIRED_MESSAGE
}

// Pulls a human-readable message out of an apiClient error. The backend returns
// a bare string, a `{ detail | message | error }` object, or DRF field errors
// like `{ image: ["Upload a valid image."] }` — normalize all of them.
export function getApiErrorMessage(error: unknown): string | undefined {
  const raw = (error as ApiError)?.response?.data
  return firstStringMessage(raw)
}

/**
 * HTTP-код ответа, если ошибка пришла от `apiClient`.
 *
 * Экраны разбирают отказы по коду: 403 на закрытый класс задач — это объяснимое
 * состояние со своим текстом, а не общий тост, и отличить его от 404 и от
 * сетевого сбоя можно только здесь. Без помощника каждый вызывающий приводил бы
 * `unknown` к своей локальной форме ошибки — а форма у неё одна на приложение.
 */
export function getApiErrorStatus(error: unknown): number | undefined {
  const status = (error as ApiError)?.response?.status
  return typeof status === 'number' ? status : undefined
}

/**
 * Границы паузы, которую `retryAfterMs` отдаёт вызывающему.
 *
 * Верхняя обязательна: `Retry-After: 86400` — штатное значение для суточного
 * окна троттлинга, и без потолка кнопка, которая ждёт эту паузу, умирала бы до
 * перезагрузки страницы — например, внутри окна входа на оплаченную олимпиаду.
 * Ждать дольше пяти минут бессмысленно: проще перезайти на страницу.
 *
 * Нижняя не даёт «паузе» в ноль (`Retry-After: 0`, дата в прошлом) стать
 * возможностью жать повтор подряд, когда попытки считаны.
 *
 * Живут здесь, а не у вызывающего: границы — свойство самой паузы, и каждый
 * новый потребитель заголовка получает их бесплатно, вместо того чтобы
 * повторить баг с сутками.
 */
export const RETRY_AFTER_MIN_MS = 5_000
export const RETRY_AFTER_MAX_MS = 5 * 60_000

/**
 * Пауза до повтора из ответа, если бэк её назвал — уже в границах
 * `RETRY_AFTER_MIN_MS`…`RETRY_AFTER_MAX_MS`.
 *
 * `Retry-After` приходит либо числом секунд, либо HTTP-датой — спецификация
 * разрешает оба вида, и оба встречаются у DRF и у прокси. Отрицательная или
 * уже прошедшая дата — это «можно сейчас», то есть нижняя граница, а не
 * отрицание.
 *
 * ⚠ Заголовок не входит в CORS-safelist: с локального дева (другой origin)
 * браузер его не отдаст, пока бэк не пришлёт
 * `Access-Control-Expose-Headers: Retry-After`. Поэтому вызывающий обязан
 * иметь свой запасной интервал, а не считать `null` за «повторять сразу».
 */
export function retryAfterMs(error: unknown): number | null {
  const value = (error as ApiError)?.response?.retryAfterMs
  if (typeof value !== 'number') return null
  return Math.min(Math.max(value, RETRY_AFTER_MIN_MS), RETRY_AFTER_MAX_MS)
}

/** HTTP разрешает только целые десятичные секунды. `Number()` принимал бы и
 *  `0x10`, и `1e3`, и пустую строку (как ноль) — то есть молча превращал бы
 *  мусор в паузу. */
const RETRY_AFTER_SECONDS = /^\d+$/

function parseRetryAfter(header: string | null): number | undefined {
  if (!header) return undefined
  const value = header.trim()

  if (RETRY_AFTER_SECONDS.test(value)) return Number(value) * 1000

  // HTTP-дата всегда содержит буквы («Wed, 21 Oct 2015 07:28:00 GMT»), а
  // `Date.parse` без этой проверки соглашается на `-5` и `1.5`, выдавая
  // случайную дату из прошлого. Мусор должен оставаться мусором.
  if (!/[a-z]/i.test(value)) return undefined

  const at = Date.parse(value)
  if (Number.isNaN(at)) return undefined
  // Дата в прошлом (частый эффект расхождения часов на прокси) — это «можно
  // сейчас», а не отрицательная пауза.
  return Math.max(0, at - Date.now())
}

function firstStringMessage(value: unknown): string | undefined {
  if (typeof value === 'string') return value || undefined
  if (Array.isArray(value)) {
    for (const item of value) {
      const msg = firstStringMessage(item)
      if (msg) return msg
    }
    return undefined
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>
    // Prefer the conventional message keys, then fall back to the first field error.
    // `code` is a machine value by API convention (`{code, detail}`), never a
    // message: an error that carries only a code must fall through to the
    // caller's localized fallback, not show `proctor_bad_redirect` on screen.
    const { code: _code, ...rest } = obj
    const ordered = [obj.detail, obj.message, obj.error, ...Object.values(rest)]
    for (const candidate of ordered) {
      const msg = firstStringMessage(candidate)
      if (msg) return msg
    }
  }
  return undefined
}

// Single in-flight promise shared by all concurrent callers — prevents duplicate
// refresh requests when AuthProvider init and API 401s race each other, which would
// cause the second caller to use an already-rotated (invalid) refresh token.
let refreshPromise: Promise<string> | null = null

export function doRefresh(): Promise<string> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    const refresh = authTokens.getRefresh()
    if (!refresh) throw Object.assign(new Error('No refresh token'), { status: 401 })
    const res = await fetch(`${API_BASE}/users/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    })
    if (!res.ok) throw Object.assign(new Error('Refresh failed'), { status: res.status })
    const data = (await res.json()) as { access: string; refresh?: string }
    authTokens.setAccess(data.access)
    if (data.refresh) authTokens.setRefresh(data.refresh)
    return data.access
  })().finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

function buildUrl(path: string, params?: FetchOptions['params']): string {
  const url = `${API_BASE}${path}`
  if (!params) return url
  const search = new URLSearchParams()
  for (const [key, val] of Object.entries(params)) {
    if (val != null && val !== '') search.set(key, String(val))
  }
  const qs = search.toString()
  return qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url
}

function buildHeaders(body?: RequestBody): Headers {
  const headers = new Headers()

  const token = authTokens.getAccess()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const backendLang = getBackendLang()
  headers.set('Accept-Language', backendLang)
  headers.set('Content-Language', backendLang)

  if (body !== undefined && body !== null && !(body instanceof FormData)) {
    headers.set('Content-Type', 'application/json')
  }

  return headers
}

async function request<T>(
  method: string,
  path: string,
  body?: RequestBody,
  options?: FetchOptions
): Promise<{ data: T }> {
  // Send the current language as a query param on every request (the backend
  // filters content by `?language=`). An explicit params.language still wins.
  const url = buildUrl(path, { language: getBackendLang(), ...options?.params })
  const headers = buildHeaders(body)

  const init: RequestInit = {
    method,
    headers,
    signal: options?.signal,
    body:
      body instanceof FormData
        ? body
        : body !== undefined && body !== null
          ? JSON.stringify(body)
          : undefined,
  }

  let res = await fetch(url, init)

  // Only attempt a token refresh + redirect for *session* 401s — i.e. when we
  // actually have a refresh token and the request wasn't itself an auth call.
  // Login/register/refresh failures should bubble up to the caller so the
  // form can show a toast or inline error instead of hard-redirecting.
  const isAuthRequest =
    path === '/users/token/' || path === '/users/token/refresh/' || path === '/users/registration/'

  if (res.status === 401 && !isAuthRequest && authTokens.getRefresh()) {
    // Выход из сессии — только если не удалось само обновление токена. Повтор
    // запроса ниже вне try/catch: его сбой (в том числе AbortError от сигнала
    // вызывающего, отменившего запрос за время медленного refresh) уходит
    // вызывающему как есть и сессию не трогает.
    let newToken: string
    try {
      newToken = await doRefresh()
    } catch {
      authTokens.clear()
      window.location.href = '/login'
      throw new Error(SESSION_EXPIRED_MESSAGE)
    }
    ;(init.headers as Headers).set('Authorization', `Bearer ${newToken}`)
    res = await fetch(url, init)
  }

  if (!res.ok) {
    const rawText = await res.text().catch(() => '')
    let errData: unknown = rawText
    try {
      errData = JSON.parse(rawText)
    } catch {
      // Body wasn't JSON. If it looks like an HTML error page (proxy/nginx 404,
      // framework stack-trace page), replace it with a clean status-based message
      // so UI toasts don't render raw `<h1>Not Found</h1>` markup.
      const trimmed = rawText.trimStart().toLowerCase()
      const isHtml = trimmed.startsWith('<!doctype') || trimmed.startsWith('<html')
      // У серверных сбоев тело — это чужая диагностика: трейс приложения,
      // страница прокси, дамп апстрима. Показывать её участнику (а тем более
      // подставлять в текст обращения в поддержку) нечего — заменяем на
      // статус. У 4xx текст обычно адресован пользователю, его оставляем.
      if (isHtml || res.status >= 500) {
        errData = { detail: res.statusText || `HTTP ${res.status}` }
      }
    }
    const err = Object.assign(new Error(`HTTP ${res.status}`), {
      response: {
        status: res.status,
        data: errData,
        retryAfterMs: parseRetryAfter(res.headers.get('Retry-After')),
      },
    })
    throw err
  }

  // 204 No Content — no body to parse
  if (res.status === 204) return { data: undefined as T }

  if (options?.responseType === 'blob') {
    return { data: (await res.blob()) as T }
  }

  const data = (await res.json()) as T
  return { data }
}

export const apiClient = {
  get: <T>(path: string, options?: FetchOptions) => request<T>('GET', path, undefined, options),

  // Binary GET for file downloads — returns the response as a Blob.
  getBlob: (path: string, options?: FetchOptions) =>
    request<Blob>('GET', path, undefined, { ...options, responseType: 'blob' }),

  post: <T = unknown>(path: string, body?: RequestBody, options?: FetchOptions) =>
    request<T>('POST', path, body, options),

  patch: <T = unknown>(path: string, body?: RequestBody, options?: FetchOptions) =>
    request<T>('PATCH', path, body, options),

  put: <T = unknown>(path: string, body?: RequestBody, options?: FetchOptions) =>
    request<T>('PUT', path, body, options),

  // Тело необязательно: большинство DELETE его не несут, но отмена регистрации
  // принимает `{reason}`. Лежит в options, чтобы существующие вызовы
  // `delete(path)` / `delete(path, { signal })` не менялись.
  delete: <T = unknown>(path: string, options?: FetchOptions & { body?: RequestBody }) =>
    request<T>('DELETE', path, options?.body, options),
}
